import { type FC, useRef, useState } from "react";
import { Loader2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import ConfirmDeleteDialog from "@/shared/components/overlay/ConfirmDeleteDialog";

import {
  SafeDepositForm,
  type SafeDepositFormValues,
  type SafeDepositFieldErrors,
} from "./SafeDepositForm";

interface EditDepositBoxDialogProps {
  editing: { form: SafeDepositFormValues } | null;
  error: string | null;
  fieldErrors?: SafeDepositFieldErrors;
  isLoading?: boolean;
  onChange: (form: SafeDepositFormValues) => void;
  onSave: () => void;
  onClose: () => void;
  onDeleteConfirm: () => void;
}

export const EditDepositBoxDialog: FC<EditDepositBoxDialogProps> = ({
  editing,
  error,
  fieldErrors,
  isLoading,
  onChange,
  onSave,
  onClose,
  onDeleteConfirm,
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  return (
    <>
      <Dialog
        open={!!editing}
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
      >
        <DialogContent ref={dialogRef} size="md">
          <DialogHeader>
            <DialogTitle>Edit safe deposit box</DialogTitle>
          </DialogHeader>
          {editing && (
            <DialogBody>
              <SafeDepositForm form={editing.form} onChange={onChange} fieldErrors={fieldErrors} />
              {error && <p className="text-sm text-destructive">{error}</p>}
            </DialogBody>
          )}
          <div className="flex justify-between pt-6">
            <Button
              type="button"
              variant="destructive"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isLoading}
            >
              Delete
            </Button>
            <Button onClick={onSave} disabled={isLoading}>
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => {
          setShowDeleteConfirm(false);
          onDeleteConfirm();
        }}
        title="Delete Safe Deposit Box"
        description={
          editing
            ? `Are you sure you want to delete "${editing.form.location}"? This action cannot be undone.`
            : "Are you sure? This action cannot be undone."
        }
        isLoading={isLoading ?? false}
      />
    </>
  );
};
